import React, { useEffect, useMemo, useState, useCallback } from "react";
import { ChevronRightIcon } from "@heroicons/react/24/solid";
import { useTranslation } from "react-i18next";
import StoreContent from "../../Store/StoreContent";
import { useProducts } from "../../../contexts/Products";
import { useServices } from "../../../contexts/Services";
import { formatDateTime } from "../../../globalFunctions/DateTimeDisplayCleaner";
import Button from "../../../components/controls/Button";

const buildSteps = (selectedServices, selectedProducts) => {
  const serviceSteps = (selectedServices || []).map((category) => ({
    type: "service",
    category,
  }));
  const productSteps = (selectedProducts || []).map((category) => ({
    type: "product",
    category,
  }));
  return [...serviceSteps, ...productSteps];
};

const canGoNext = (index, steps) => index < steps.length - 1;
const nextIndex = (index, steps) =>
  canGoNext(index, steps) ? index + 1 : index;

const normalizeCategories = (source) => {
  if (Array.isArray(source)) return source;
  if (Array.isArray(source?.categories)) return source.categories;
  if (Array.isArray(source?.items)) return source.items;
  if (Array.isArray(source?.data)) return source.data;
  return [];
};

const getCategoryKey = (category) =>
  category?.key ??
  category?.value ??
  category?.slug ??
  category?.id ??
  category?.code ??
  "";

const getCategoryLabel = (category, lang) => {
  if (!category) return "";
  const isFrench = (lang || "").toLowerCase().startsWith("fr");
  return (
    (isFrench ? category?.nameFr : category?.nameEn) ??
    category?.label ??
    category?.name ??
    category?.title ??
    ""
  );
};

const buildCategoryMap = (rawCategories, lang) => {
  const list = normalizeCategories(rawCategories);
  return list.reduce((map, category) => {
    const key = `${getCategoryKey(category)}`;
    if (!key) return map;
    map[key] = getCategoryLabel(category, lang) || key;
    return map;
  }, {});
};

export default function ProductServiceSelection({
  nextStep,
  updateEventData,
  eventData,
  returnToCategorySelection,
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const productCategories = useProducts();
  const serviceCategories = useServices();

  const productCategoryMap = useMemo(
    () => buildCategoryMap(productCategories, lang),
    [productCategories, lang]
  );

  const serviceCategoryMap = useMemo(
    () => buildCategoryMap(serviceCategories, lang),
    [serviceCategories, lang]
  );

  const steps = useMemo(
    () => buildSteps(eventData?.selectedServices, eventData?.selectedProducts),
    [eventData?.selectedServices, eventData?.selectedProducts]
  );

  const [stepIndex, setStepIndex] = useState(0);
  const [autoSkippedKey, setAutoSkippedKey] = useState("");

  useEffect(() => {
    if (!steps.length) returnToCategorySelection();
    if (stepIndex >= steps.length) setStepIndex(0);
  }, [steps.length, stepIndex, returnToCategorySelection]);

  const current = steps[stepIndex] || null;
  const location = eventData?.location || null;

  const categoryLabel = useMemo(() => {
    if (!current) return "";
    const key = `${current.category}`;
    if (current.type === "service") return serviceCategoryMap[key] || key;
    return productCategoryMap[key] || key;
  }, [current, serviceCategoryMap, productCategoryMap]);

  const handleNext = useCallback(() => {
    if (!current) return;
    if (canGoNext(stepIndex, steps))
      return setStepIndex(nextIndex(stepIndex, steps));
    nextStep();
  }, [current, stepIndex, steps, nextStep]);

  const handleStoreResult = useCallback(
    ({ totalCount }) => {
      if (!current) return;
      if (totalCount > 0) return;
      const key = `${current.type}|${current.category}|${stepIndex}`;
      if (autoSkippedKey === key) return;
      setAutoSkippedKey(key);
      if (canGoNext(stepIndex, steps))
        setStepIndex(nextIndex(stepIndex, steps));
      else nextStep();
    },
    [current, stepIndex, steps, autoSkippedKey, nextStep]
  );

  if (!current) return null;

  return (
    <div className="min-h-screen">
      <div className="px-6 pt-6">
        <div className="flex flex-wrap gap-2 mt-3">
          <span className="px-4 py-1 rounded-full bg-primary text-white">
            {current.type === "service"
              ? t("storeFilters.services")
              : t("storeFilters.products")}
          </span>

          <span className="px-4 py-1 rounded-full bg-primary text-white">
            {categoryLabel}
          </span>

          {!!location?.formattedAddress && (
            <span className="px-4 py-1 rounded-full bg-primary text-white">
              {location.formattedAddress}
            </span>
          )}

          {!!eventData?.eventDate && !!eventData?.eventEndDate && (
            <span className="px-4 py-1 rounded-full bg-primary text-white flex items-center gap-1">
              {formatDateTime(eventData.eventDate)}
              <ChevronRightIcon className="w-4 h-4"></ChevronRightIcon>
              {formatDateTime(eventData.eventEndDate)}
            </span>
          )}
        </div>
      </div>

      <div className="p-6">
        <StoreContent
          showFilters={false}
          showCartSidebar={true}
          showFloatingCartButton={true}
          showViewCartButton={false}
          lockedType={current.type}
          fixedCategory={current.category}
          eventFilters={{
            startDateTime: eventData?.eventDate,
            endDateTime: eventData?.eventEndDate,
            latitude: location?.latitude ?? null,
            longitude: location?.longitude ?? null,
            radiusKm: 60,
          }}
          onResult={handleStoreResult}
        />
      </div>

      <Button
        as="button"
        type="button"
        onClick={handleNext}
        color="primary"
        size="lg"
        rightIcon={<ChevronRightIcon className="w-6 h-6" />}
        className="fixed bottom-8 right-8 z-50 text-lg font-semibold animate-scaleUpDown"
      >
        {canGoNext(stepIndex, steps)
          ? t("productServiceSelection.nextCategory")
          : t("productServiceSelection.finishSelection")}
      </Button>
    </div>
  );
}
